/**
 * Collection color swatches component that displays matching product variants
 * and allows switching between them with simple a href links
 */

class CollectionColorSwatches {
  constructor() {
    this.productCards = document.querySelectorAll(".product-item");
    this.products = new Map(); // Cache for product data
    this.init();
    this.setupMutationObserver();
  }

  async init() {
    if (!this.productCards.length) return;
    
    for (const card of this.productCards) {
      await this.processProductCard(card);
    }
  }

  async processProductCard(card) {
    const productHandle = card.dataset.productHandle;
    if (!productHandle) return;

    const swatchContainer = card.querySelector(".js-card-color-swatches");
    if (!swatchContainer) return;

    const sku = card.dataset.productSku;
    
    if (!sku) {
      console.warn(`No SKU found for product: ${productHandle}`);
      swatchContainer.style.display = "none";
      return;
    }

    await this.renderProductSwatches(sku, productHandle, swatchContainer);
  }

  setupMutationObserver() {
    const productGrid = document.querySelector("#CollectionLoop");
    if (!productGrid) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.classList && node.classList.contains("product-item")) {
                this.processProductCard(node);
              } else {
                const productCards = node.querySelectorAll(".product-item");
                if (productCards.length) {
                  productCards.forEach((card) => this.processProductCard(card));
                }
              }
            }
          });
        }
      });
    });

    observer.observe(productGrid, { childList: true, subtree: true });
  }

  async fetchRelatedProducts(sku) {
    // Check cache first
    if (this.products.has(sku)) {
      return this.products.get(sku);
    }

    try {
      const base = `${window.Shopify?.routes?.root || ""}apps/marvya-variants`;
      const response = await fetch(`${base}/${sku}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      
      // Cache the result
      this.products.set(sku, data);
      return data;
    } catch (error) {
      console.error("Error fetching related products:", error);
      return null;
    }
  }

  async renderProductSwatches(sku, currentHandle, container) {
    try {
      const data = await this.fetchRelatedProducts(sku);
      
      if (!data || !data.edges || data.edges.length === 0) {
        container.style.display = "none";
        return;
      }

      this.renderSwatches(data.edges, currentHandle, container);
    } catch (error) {
      console.error("Error rendering product swatches:", error);
      container.style.display = "none";
    }
  }

  renderSwatches(products, currentHandle, container) {
    if (!products.length) {
      container.style.display = "none";
      return;
    }

    let html = '<div class="card-color-swatches">';

    // Limit display to first 3 swatches if there are more than 3
    const maxSwatchesToShow = 3;
    const totalSwatches = products.length;
    const displayProducts = totalSwatches > maxSwatchesToShow 
      ? products.slice(0, maxSwatchesToShow) 
      : products;

    displayProducts.forEach(({ node: product }) => {
      const isActive = product.handle === currentHandle;
      const activeClass = isActive ? "is-active" : "";

      // Get color data from metafield references
      const colorData = product.metafield?.references?.edges[0]?.node?.fields || [];
      const colorLabel = colorData.find(field => field.key === "label")?.value || product.title;
      const colorHex = colorData.find(field => field.key === "color")?.value || "#FFFFFF";

      html += `
        <a href="/products/${product.handle}" 
           class="card-color-swatch ${activeClass}" 
           title="${colorLabel}"
           style="background-color: ${colorHex}">
        </a>
      `;
    });

    // Add "+x more colors" button if there are more than maxSwatchesToShow
    if (totalSwatches > maxSwatchesToShow) {
      const extraColors = totalSwatches - maxSwatchesToShow;
      html += `
        <a href="/products/${currentHandle}" 
           class="card-color-swatch--more" 
           title="View ${extraColors} more color${extraColors > 1 ? "s" : ""}">
           +${extraColors} more colors
        </a>
      `;
    }

    html += "</div>";
    container.innerHTML = html;
  }
}

// Initialize for collection pages
document.addEventListener("DOMContentLoaded", () => {
  if (document.querySelector(".collection")) {
    new CollectionColorSwatches();
  }
});

// Initialize when the AJAX pagination loads new content
document.addEventListener("product-grid:loaded", () => {
  if (document.querySelector(".collection")) {
    new CollectionColorSwatches();
  }
});

export default CollectionColorSwatches;
